import { useState, useCallback, useMemo } from 'react';
import { uploadService } from '../services/uploadService';
import { isPDBFile } from '../utils/fileHelpers';

export function useUploadManagement(initialContext = {}) {
  // Upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedUploadIds, setSelectedUploadIds] = useState(new Set());
  const [uploadProject, setUploadProject] = useState(initialContext.project || '');
  const [uploadTarget, setUploadTarget] = useState(initialContext.target || '');
  const [uploadBatchName, setUploadBatchName] = useState(initialContext.batch || '');
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Computed values
  const selectedUploadFileRows = useMemo(() => 
    uploadedFiles.filter(file => selectedUploadIds.has(file.id)),
    [uploadedFiles, selectedUploadIds]
  );

  // Transform upload files for display in grid
  const transformedUploadFiles = useMemo(() => {
    return uploadedFiles.map((file, index) => ({
      id: index,
      id_: index + 1,
      source_path: file.webkitRelativePath || file.name || '',
      origin: { source_path: file.webkitRelativePath || file.name || '' },
      project: uploadProject,
      target: uploadTarget,
      mutation: '',
      subgroup: '',
      prep_workflow: '',
      batch: uploadBatchName,
      ligand_name: null,
      ligand_tag: '',
      author: '',
      notes: '',
      prepared_at: null,
      created_at: new Date().toISOString(),
      tags: '',
      webkitRelativePath: file.webkitRelativePath,
      name: file.name,
      _file: file // Store the actual File object reference
    }));
  }, [uploadedFiles, uploadProject, uploadTarget, uploadBatchName]);

  // Get upload context
  const getUploadContext = useCallback(() => {
    const project = uploadProject || 'New Project';
    const target = uploadTarget || 'New Target';
    const batch = uploadBatchName || 'New Batch';
    
    return {
      project: uploadProject,
      target: uploadTarget,
      batch: uploadBatchName,
      suggestedPath: `${project}/${target}/${batch}`
    };
  }, [uploadProject, uploadTarget, uploadBatchName]);

  // Handle files being added
  const handleFilesUploaded = useCallback((files, options = {}) => {
    const { onSuccess, onWarning } = options;
    const uploadContext = getUploadContext();
    
    // Filter out non-PDB files
    const pdbFiles = files.filter(file => isPDBFile(file.name));
    
    if (pdbFiles.length === 0) {
      onWarning?.('No PDB files found in selection');
      return;
    }
    
    // Clear selection when adding new files to prevent ID mismatch
    setSelectedUploadIds(new Set());
    
    const processedFiles = pdbFiles.map((file, index) => ({
      id: index + uploadedFiles.length,
      name: file.name || '',
      webkitRelativePath: file.webkitRelativePath || '',
      filename: file.name || '',
      file: file,
      size: file.size || 0,
      ligand_name: null,
      author: '',
      uploaded_at: new Date().toISOString()
    }));
    
    setUploadedFiles(prev => [...prev, ...processedFiles]);
    
    // Show different message if some files were filtered
    if (pdbFiles.length < files.length) {
      const filtered = files.length - pdbFiles.length;
      onSuccess?.(`Added ${pdbFiles.length} PDB file(s), filtered out ${filtered} non-PDB file(s)`, 'info');
    } else {
      onSuccess?.(`Successfully added ${pdbFiles.length} file(s) to ${uploadContext.suggestedPath}`);
    }
    
    return pdbFiles.length;
  }, [uploadedFiles.length, uploadProject, uploadTarget, uploadBatchName, getUploadContext]);

  // Submit upload
  const handleSubmitUpload = useCallback(async (options = {}) => {
    const {
      previewData = [],
      isPreviewMode = false,
      user,
      onProgress,
      onSuccess,
      onError,
      onComplete
    } = options;

    try {
      if (uploadedFiles.length === 0) {
        onError?.('No files to upload');
        return null;
      }

      setIsUploading(true);
      onProgress?.(`Uploading ${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}...`);

      const response = await uploadService.uploadFiles({
        uploadedFiles,
        previewData,
        isPreviewMode,
        user,
        uploadProject,
        uploadTarget,
        uploadBatchName,
        onProgress: (progress) => {
          if (progress.status === 'processing') {
            onProgress?.(progress.message);
          }
        }
      });
      
      if (response.status === 201 || response.status === 200) {
        // Clear the upload files and selection
        setUploadedFiles([]);
        setSelectedUploadIds(new Set());
        
        onSuccess?.(`Successfully uploaded ${uploadedFiles.length} file(s)`);
        onComplete?.();
        
        return response;
      } else if (response.status !== 202) {
        console.warn('Unexpected response status:', response.status);
        onError?.(`Upload completed with unexpected status ${response.status}`);
        return response;
      }
      
      return response;
    } catch (error) {
      console.error('Upload error:', error);
      onError?.(error.response?.data?.detail || error.message);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, [uploadedFiles, uploadProject, uploadTarget, uploadBatchName]);

  // Clear upload files
  const handleClearUploadFiles = useCallback(() => {
    setUploadedFiles([]);
    setSelectedUploadIds(new Set());
  }, []);

  // Update upload context from tree selection
  const updateUploadContext = useCallback((nodeInfo) => {
    if (nodeInfo?.project) setUploadProject(nodeInfo.project);
    if (nodeInfo?.target) setUploadTarget(nodeInfo.target);
    if (nodeInfo?.batch) setUploadBatchName(nodeInfo.batch);
  }, []);

  // Generate upload suggestions based on existing data
  const generateUploadSuggestions = useCallback((prepFiles = []) => {
    const allFiles = [...prepFiles, ...uploadedFiles];
    
    const extractUniqueValues = (field, filters = {}) => {
      const values = new Set();
      allFiles.forEach(file => {
        // Apply filters if specified
        for (const [filterField, filterValue] of Object.entries(filters)) {
          if (filterValue && file[filterField] !== filterValue) {
            return;
          }
        }
        
        const value = file[field];
        if (value && value !== '' && value !== '<none>') {
          values.add(value);
        }
      });
      return Array.from(values).sort();
    };
    
    return {
      project: extractUniqueValues('project'),
      target: uploadProject 
        ? extractUniqueValues('target', { project: uploadProject })
        : extractUniqueValues('target'),
      batch: uploadProject || uploadTarget
        ? extractUniqueValues('batch', { 
            ...(uploadProject && { project: uploadProject }),
            ...(uploadTarget && { target: uploadTarget })
          })
        : extractUniqueValues('batch')
    };
  }, [uploadedFiles, uploadProject, uploadTarget]);

  return {
    // State
    uploadedFiles,
    setUploadedFiles,
    selectedUploadIds,
    setSelectedUploadIds,
    uploadProject,
    setUploadProject,
    uploadTarget,
    setUploadTarget,
    uploadBatchName,
    setUploadBatchName,
    isUploading,
    setIsUploading,
    isDraggingOver,
    setIsDraggingOver,
    
    // Computed
    selectedUploadFileRows,
    transformedUploadFiles,
    
    // Methods
    handleFilesUploaded,
    handleSubmitUpload,
    handleClearUploadFiles,
    getUploadContext,
    updateUploadContext,
    generateUploadSuggestions
  };
}